import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ProjectAccessResponse, ProjectsClient } from '../../web-api-client';
import { authenticatedFetch, getApiBaseUrl } from '../../api/apiConfig';
import { Permissions, usePermissions } from '../../contexts/PermissionContext';
import LoadingWheel from '../shared/LoadingWheel';

const ProjectStakeholdersPanel: React.FC<{ projectId: string }> = ({ projectId }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const client = useMemo(() => new ProjectsClient(getApiBaseUrl(), authenticatedFetch), []);
  const [access, setAccess] = useState<ProjectAccessResponse | null>(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try { setAccess(await client.getApiProjectsStakeholders(projectId)); }
    catch { setError(true); }
  }, [client, projectId]);
  useEffect(() => { void load(); }, [load]);
  const update = async (userId?: string) => {
    setSaving(true); setError(false);
    try {
      await client.postApiProjectsStakeholders(projectId, { userId: userId ?? '00000000-0000-0000-0000-000000000000', remove: !!userId, email: userId ? undefined : email.trim() });
      setEmail(''); await load();
    } catch { setError(true); }
    finally { setSaving(false); }
  };
  const canManage = access?.canManage && hasPermission(Permissions.PROJECT_MANAGE);
  return <Stack spacing={6}>
    {error && <Alert severity="error">{t('projects.accessFailed')}</Alert>}
    {!access && !error && <LoadingWheel />}
    <Typography color="text.secondary">{t('projects.accessPolicy')}</Typography>
    {access?.members?.map(member => <Paper key={member.userId} variant="outlined" sx={{ p: 8 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography>{member.name} · {t(member.isCreator ? 'projects.creator' : 'projects.stakeholder')}</Typography>
        {canManage && !member.isCreator && <Button size="md" disabled={saving} onClick={() => void update(member.userId)}>{t('projects.removeAccess')}</Button>}
      </Stack>
    </Paper>)}
    {canManage && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
      <TextField label={t('projects.stakeholderEmail')} type="email" value={email} onChange={e => setEmail(e.target.value)} disabled={saving} fullWidth />
      <Button variant="primary" size="md" sx={{ minWidth: 190, flexShrink: 0, whiteSpace: 'nowrap' }} disabled={saving || !email.trim()} onClick={() => void update()}>{t('projects.addStakeholder')}</Button>
    </Stack>}
  </Stack>;
};

export default ProjectStakeholdersPanel;
